import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import styled from 'styled-components';
import { isNotTransits, isPrognostics, isRelocation } from '@chronos/astro-lib';
import { calcHorarSigns, highlightAspect, useTranslation, defaultPersonalization } from './utils';
import Objects from './Objects';
import Aspects from './Aspects';
import Zodiacs from './Zodiacs';
import Houses from './Houses';
import SoulObjects from './SoulObjects';
import FixedStars from './FixedStars';
import { radialPoint } from '@chronos/api';
export function getSectorLength(i, values) {
    const lon = values[i];
    let nlon = values[(i + 1) % 12];
    if (nlon < lon) {
        nlon += 360;
    }
    return nlon - lon;
}
export const WITH_OUTER_OFFSET = 0.07;
export default function Circle(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const personalization = props.mapSettings.personalization;
    const computedRadius = (props.mapSettings.personalization.computedRadius || defaultPersonalization.circle.radius);
    const { prognosticsNatal, compatibilityTransits } = props.mapSettings.indicator || {};
    const compatibilityTransitMode2 = ((_a = compatibilityTransits === null || compatibilityTransits === void 0 ? void 0 : compatibilityTransits[props.mode]) === null || _a === void 0 ? void 0 : _a.transitMode) === 2;
    const hasExt = Boolean((_b = props.housesExt) === null || _b === void 0 ? void 0 : _b.length) && !isRelocation(props.mode);
    const hasOuter = Boolean((_c = props.housesOuter) === null || _c === void 0 ? void 0 : _c.length) && !isRelocation(props.mode);
    const { t } = useTranslation();
    const objects = props.objects;
    const houses = props.houses;
    const { startingPoint: startPoint, rotationDir, showHigherPlanets, showMinorPlanets } = props.mapSettings.astroSettings || {};
    let rotation;
    if (props.mode === 'horar') {
        rotation = 180 - (houses[0] || 0);
    }
    else if (props.mode === 'soul') {
        rotation = 180;
    }
    else {
        if (startPoint === 'aries') {
            if (rotationDir === 'cw') {
                rotation = 0;
            }
            else {
                rotation = 180;
            }
        }
        else {
            if (rotationDir === 'cw') {
                rotation = 180 - (houses[0] - 180 || 0);
            }
            else {
                if (isRelocation(props.mode)) {
                    rotation = props.mode === 'relocation_natal' ? 180 - (houses[0] || 0) : 180 - props.housesExt[0];
                }
                else {
                    rotation = 180 - (houses[0] || 0);
                }
            }
        }
    }
    const partProps = {
        mode: props.mode,
        x: props.x,
        y: props.y,
        hasExt,
        hasOuter,
        radius: props.r,
        point: (r, a) => {
            if (props.mode === 'horar') {
                return radialPoint(props.x, props.y, r, a + (props.mode === 'horar' ? 0 : rotation));
            }
            else if (props.mode === 'soul') {
                return radialPoint(props.x, props.y, r, a + (rotation));
            }
            else {
                if (rotationDir === 'ccw') {
                    return radialPoint(props.x, props.y, r, a + (rotation));
                }
                else {
                    return radialPoint(props.x, props.y, r, -(a + (rotation)));
                }
            }
        },
        onHelp: (category, id) => {
            var _a, _b;
            if (props.mapSettings.access.limited) {
                (_b = (_a = props.mapSettings.access) === null || _a === void 0 ? void 0 : _a.limitedAction) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            else {
                onHelpNew(category, id);
            }
        },
        onHover: (what, id) => {
            if (props.mapSettings.access.limited)
                return false;
            if (id === -1) {
                props.onChanged('highlights', []);
            }
            else if (what === 'aspect') {
                props.onChanged('highlights', highlightAspect(props.mode, props.aspects[id], hasExt, hasOuter));
            }
        },
        rotation,
        isEditor: props.isEditor,
        doubleMapWithOutNatal: !(prognosticsNatal === null || prognosticsNatal === void 0 ? void 0 : prognosticsNatal[props.mode]),
        computedRadius,
        mapSettings: props.mapSettings,
    };
    const onHelpNew = (category, id) => {
        if (props.mode !== 'natal' || category === 'objects' && id >= 10) {
            return;
        }
        props.onChanged('prompt', { category, id });
    };
    const { radius } = personalization.circle || {};
    const radiusModified = {
        internalSoul: props.r * ((radius === null || radius === void 0 ? void 0 : radius.internalSoul) || 0.1),
        internalHorar: props.r * (radius === null || radius === void 0 ? void 0 : radius.internalHorar),
        internal: props.r * ((hasExt && !hasOuter) ? computedRadius.internalExt : (hasOuter && hasExt ? (computedRadius.internalExt - WITH_OUTER_OFFSET) : computedRadius.internal)),
        zodiacsInternal: props.r * (hasExt ? computedRadius.zodiacsInternalExt : computedRadius.zodiacsInternal),
        zodiacsExternal: props.r * (hasExt ? computedRadius.zodiacsExternalExt : computedRadius.zodiacsExternal),
        external: props.r * (radius === null || radius === void 0 ? void 0 : radius.external),
        outer: props.r * (computedRadius.zodiacsExternalExt),
        middle: props.r * (computedRadius.internalExt + (WITH_OUTER_OFFSET))
    };
    const getInternalRadius = (mode) => {
        switch (mode) {
            case 'horar': return radiusModified.internalHorar;
            case 'soul': return radiusModified.internalSoul || 0.1;
            default: return radiusModified.internal;
        }
    };
    const getDelimeterRadius = (index) => {
        const objectsRadius = radiusModified.zodiacsInternal - (radiusModified.internalSoul || 0.1);
        if (index === 0) {
            return objectsRadius / 8 * index + (radiusModified.internalSoul || 0.1);
        }
        return objectsRadius / 8 * (index + 1) + (radiusModified.internalSoul || 0.1);
    };
    const highlights = (((_e = (_d = props.highlights) === null || _d === void 0 ? void 0 : _d.items) === null || _e === void 0 ? void 0 : _e.length) || 0) === 0 ? null : props.highlights.items;
    const hlAspects = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'aspect').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlObjects = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'object').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlObjectsExt = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'object_ext').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlObjectsOuter = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'object_outer').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlHouses = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'house').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlHousesExt = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'house_ext').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const hlSigns = (highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'sign').map(h => h.id)) || (compatibilityTransitMode2 ? [-1] : undefined);
    const horarSigns = props.mode === 'horar' ? calcHorarSigns(props.houses) : [];
    const hlStars = highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'star').map(h => h.id);
    const hlStarsExt = highlights === null || highlights === void 0 ? void 0 : highlights.filter(h => h.type === 'star_ext').map(h => h.id);
    const soulCircles = () => Array.from({ length: 7 }, (v, k) => k).map(i => _jsx("circle", { cx: props.x, cy: props.y, r: getDelimeterRadius(i), stroke: "var(--circle-border)", fill: "transparent" }, `soul-circle-${i}`));
    const staticCircles = useMemo(() => {
        var _a, _b, _c, _d;
        return _jsxs(_Fragment, { children: [hasExt &&
                    _jsx("circle", { cx: props.x, cy: props.y, r: radiusModified.external, fill: "var(--workspace-background)", stroke: "var(--circle-border)" }), _jsx("circle", { cx: props.x, cy: props.y, r: radiusModified.zodiacsExternal, stroke: ((_b = (_a = personalization.circle) === null || _a === void 0 ? void 0 : _a.zodiacs) === null || _b === void 0 ? void 0 : _b.style) === 0 ? 'var(--circle-zodiacs-border)' : 'none', fill: ((_d = (_c = personalization.circle) === null || _c === void 0 ? void 0 : _c.zodiacs) === null || _d === void 0 ? void 0 : _d.style) === 0 ? 'var(--circle-zodiacs-background)' : 'none' }), _jsx("circle", { cx: props.x, cy: props.y, r: radiusModified.zodiacsInternal, fill: "var(--workspace-background)" }), props.mode === 'soul' && soulCircles(), hasOuter &&
                    _jsx("circle", { cx: props.x, cy: props.y, r: radiusModified.middle, stroke: "var(--circle-border)", fill: "var(--workspace-background)" }), _jsx("circle", { cx: props.x, cy: props.y, r: getInternalRadius(props.mode), stroke: "var(--circle-border)", fill: "var(--circle-internal-background)" })] });
    }, [props.x, props.y, props.mode, hasExt, radiusModified, personalization]);
    return (_jsxs(Container, { id: props.mode, children: [staticCircles, isRelocation(props.mode) && props.mode !== 'relocation_natal'
                ? _jsx(Houses, { ...partProps, houses: props.housesExt, highlights: hlHouses })
                : _jsx(Houses, { ...partProps, houses: houses, highlights: hlHouses }), (props.mode.includes('compatibility') || isNotTransits(props.mode) && props.mode !== 'prog_prog') &&
                _jsx(Houses, { ...partProps, isExt: true, houses: props.housesExt, highlights: hlHousesExt }), _jsx(Zodiacs, { ...partProps, highlights: hlSigns, horarSigns: horarSigns, direction: rotationDir }), (props.mode !== 'horar' || props.mapSettings.showHorarAspects) &&
                _jsx(Aspects, { ...partProps, radius: props.mode === 'horar' ? getInternalRadius(props.mode) : partProps.radius, aspects: ((_g = (_f = props.highlights) === null || _f === void 0 ? void 0 : _f.aspects) === null || _g === void 0 ? void 0 : _g.length)
                        ? (_h = props.highlights) === null || _h === void 0 ? void 0 : _h.aspects
                        : (((_j = props.transitNatalAspects) === null || _j === void 0 ? void 0 : _j.length) || ((_k = props.transitPartnerAspects) === null || _k === void 0 ? void 0 : _k.length))
                            ? [...props.transitNatalAspects, ...props.transitPartnerAspects]
                            : props.aspects, strongs: props.strongs, highlights: hlAspects, objects: (_l = (props.mode === 'horar' ? props.objects : props.objectsExt)) !== null && _l !== void 0 ? _l : [], minorAspectsMode: props.minorAspectsMode, houses: houses, horarSigns: horarSigns, synastryTransits: hasOuter }), props.mode === 'soul' && _jsx(SoulObjects, { ...partProps, soulObjects: props.soulObjects }), hasOuter &&
                _jsx(Objects, { ...partProps, isOuter: true, objects: props.objectsOuter, houses: [], strongs: props.strongs, highlights: hlObjectsOuter, horarSigns: horarSigns, mode: props.mode, showMinorPlanets: props.mode !== 'soul' && showMinorPlanets }), hasExt &&
                _jsx(Objects, { ...partProps, isExt: true, isOuter: false, objects: props.objectsExt, houses: [], strongs: props.strongs, highlights: hlObjectsExt, horarSigns: horarSigns, mode: props.mode, showMinorPlanets: props.mode !== 'soul' && showMinorPlanets }), _jsx(Objects, { ...partProps, isOuter: false, objects: objects, houses: houses, strongs: props.strongs, highlights: hlObjects, horarSigns: horarSigns, soulStrongs: props.soulStrongs, chains: props.chains, showHigherPlanets: (props.mode === 'horar') && showHigherPlanets, showMinorPlanets: props.mode !== 'soul' && showMinorPlanets }), isPrognostics(props.mode)
                ? _jsxs(_Fragment, { children: [_jsx(FixedStars, { ...partProps, mode: 'natal', isExt: false, fixedStars: (prognosticsNatal === null || prognosticsNatal === void 0 ? void 0 : prognosticsNatal[props.mode]) ? props.natalFixedStars : [], objects: objects, houses: houses, highlights: hlStars, horarSigns: horarSigns, soulStrongs: props.soulStrongs }), _jsx(FixedStars, { ...partProps, isExt: true, fixedStars: (props.showFixedStars && (prognosticsNatal === null || prognosticsNatal === void 0 ? void 0 : prognosticsNatal[props.mode])) ? props.fixedStars : [], objects: props.objectsExt, houses: props.housesExt, highlights: hlStarsExt, horarSigns: horarSigns, soulStrongs: props.soulStrongs })] })
                : _jsx(FixedStars, { ...partProps, fixedStars: props.showFixedStars ? props.fixedStars : [], objects: objects, houses: houses, highlights: hlStars, horarSigns: horarSigns, soulStrongs: props.soulStrongs })] }));
}
;
export const Container = styled.g `
  /* & * {
    transition: all 0.25s ease-in-out;
  } */
`;
export const Group = styled.g `
  cursor: pointer;
`;
